import { CloudOff, Refresh, Wifi } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { useBackendConnection } from '../../hooks/useBackendConnection';
import { syncFromBackend } from '../../services/syncService';
import { logger } from '../../utils/logger';

interface ConnectionStatusProps {
  variant?: 'chip' | 'banner' | 'minimal';
  showDetails?: boolean;
}

export const ConnectionStatus: React.FC<ConnectionStatusProps> = ({
  variant = 'chip',
  showDetails = false,
}) => {
  const { isConnected, isChecking, lastChecked, error, checkConnection, clearError } =
    useBackendConnection();
  const { showSuccess, showError: showErrorNotification } = useNotification();
  const [showError, setShowError] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);

  const getStatusColor = () => {
    if (isChecking) return 'warning';
    if (!isConnected) return 'error';
    return 'success';
  };

  const getStatusText = () => {
    if (isChecking) return 'Connecting...';
    if (!isConnected) return 'Backend Offline';
    return 'Connected';
  };

  const getStatusIcon = () => {
    if (isChecking) return <Refresh sx={{ animation: 'spin 1s linear infinite' }} />;
    if (!isConnected) return <CloudOff />;
    return <Wifi />;
  };

  const handleSync = async () => {
    if (isSyncing) return;

    setIsSyncing(true);
    clearError();

    try {
      // First check connection
      await checkConnection();

      // If connected, sync data from backend to frontend
      logger.info('Syncing data from backend...');
      const result = await syncFromBackend();

      if (result.success) {
        const syncedItems: string[] = [];
        if (result.profileSynced) syncedItems.push('profile');
        if (result.planSynced) syncedItems.push('training plan');

        if (syncedItems.length > 0) {
          showSuccess(`Synced ${syncedItems.join(' and ')} from backend`);
          // Trigger refresh of the UI by dispatching an event
          window.dispatchEvent(new Event('data-synced-from-backend'));
        } else {
          showSuccess('No data found on backend');
        }
      } else {
        showErrorNotification(`Sync failed: ${result.errors.join(', ')}`);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      showErrorNotification(`Sync error: ${errorMsg}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleRetry = () => {
    clearError();
    checkConnection();
  };

  const handleDismiss = () => {
    setShowError(false);
    clearError();
  };

  // Chip variant - compact indicator
  if (variant === 'chip') {
    return (
      <Chip
        icon={
          isSyncing ? <Refresh sx={{ animation: 'spin 1s linear infinite' }} /> : getStatusIcon()
        }
        //label={getStatusText()}
        color={getStatusColor() as any}
        size="small"
        clickable={!isSyncing}
        onClick={isConnected ? handleSync : handleRetry}
        disabled={isSyncing}
        title={
          isSyncing
            ? 'Syncing data from backend...'
            : isConnected
              ? 'Click to sync data from backend'
              : 'Click to retry connection'
        }
        sx={{
          cursor: isSyncing ? 'wait' : 'pointer',
          '& .MuiChip-icon': {
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          },
        }}
      />
    );
  }

  // Minimal variant - just icon
  if (variant === 'minimal') {
    return (
      <IconButton
        size="small"
        onClick={isConnected ? handleSync : handleRetry}
        disabled={isChecking || isSyncing}
        color={getStatusColor() as any}
        title={
          isSyncing
            ? 'Syncing data from backend...'
            : isConnected
              ? 'Click to sync data from backend'
              : error || getStatusText()
        }
        sx={{
          '@keyframes spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' },
          },
        }}
      >
        {isSyncing ? <Refresh sx={{ animation: 'spin 1s linear infinite' }} /> : getStatusIcon()}
      </IconButton>
    );
  }

  // Banner variant - full error display
  return (
    <Box sx={{ width: '100%' }}>
      <Collapse in={!isConnected && showError && !!error}>
        <Alert
          severity="error"
          action={
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                color="inherit"
                size="small"
                onClick={handleRetry}
                disabled={isChecking}
                startIcon={<Refresh />}
              >
                {isChecking ? 'Connecting...' : 'Retry'}
              </Button>
              <IconButton aria-label="close" color="inherit" size="small" onClick={handleDismiss}>
                ✕
              </IconButton>
            </Box>
          }
          sx={{
            mb: 2,
            '& .MuiAlert-action': {
              alignItems: 'center',
            },
          }}
        >
          <AlertTitle>Backend Connection Failed</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {error}
          </Typography>
          {showDetails && (
            <Typography variant="caption" color="text.secondary">
              • Check if the backend server is running on localhost:3000
              <br />
              • Verify your network connection
              <br />• Contact support if the problem persists
              {lastChecked && (
                <>
                  <br />
                  Last checked: {lastChecked.toLocaleTimeString()}
                </>
              )}
            </Typography>
          )}
        </Alert>
      </Collapse>
    </Box>
  );
};

export default ConnectionStatus;
